import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  isDevMode,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { CalendarCell, CalendarUserEvent, SimCalendarBodyComponent, SimCalendarCellClassFunction } from './calendar-body.component';
import { DateRange } from './date-selection-model';
import { createMissingDateImplError } from './datepicker-errors';
import { DateFilterFn } from './datepicker-input-base';

/**
 * 一个内部组件，用于在日期选择器中显示单个年份。
 */
@Component({
  selector: 'sim-year-view',
  templateUrl: './year-view.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'sim-year-view'
  }
})
export class SimYearViewComponent<D> implements AfterContentInit {
  /** 在年视图中显示的日期（除年以外的所有内容都将被忽略） */
  @Input()
  get activeDate(): D {
    return this._activeDate;
  }
  set activeDate(value: D) {
    const oldActiveDate = this._activeDate;
    const validDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value)) || this._dateAdapter.today();
    this._activeDate = this._dateAdapter.clampDate(validDate, this.minDate, this.maxDate);
    if (this._dateAdapter.getYear(oldActiveDate) !== this._dateAdapter.getYear(this._activeDate)) {
      this._init();
    }
  }

  /** 当前选定的日期 */
  @Input()
  get selected(): DateRange<D> | D | null {
    return this._selected;
  }
  set selected(value: DateRange<D> | D | null) {
    if (value instanceof DateRange) {
      this._selected = value;
    } else {
      this._selected = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
    }

    this._setSelectedMonth(value);
  }

  /** 最小选择日期 */
  @Input()
  get minDate(): D | null {
    return this._minDate;
  }
  set minDate(value: D | null) {
    this._minDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  /** 最大选择日期 */
  @Input()
  get maxDate(): D | null {
    return this._maxDate;
  }
  set maxDate(value: D | null) {
    this._maxDate = this._dateAdapter.getValidDateOrNull(this._dateAdapter.deserialize(value));
  }

  /** 用于过滤哪些日期可选的函数 */
  @Input() dateFilter: DateFilterFn<D | null>;

  @Input() dateClass: SimCalendarCellClassFunction<D>;

  /**
   * 选择面板 决定 startView 值
   * year 选择多年面板 => multi-year
   * month 选择月份面板 => multi-year | year
   * week 选择周面板 => multi-year | year | month
   * date 选择日期面板 => multi-year | year | month
   * date-time 选择日期时间面板 => multi-year | year | month | date
   */
  @Input() picker: 'year' | 'month' | 'week' | 'date' | 'datetime' = 'date';

  /** 选择新月份时发出 */
  @Output() readonly selectedChange: EventEmitter<D> = new EventEmitter();

  /** 发出选定的月份。 当 picker为 'month' 发出 */
  @Output() readonly userSelection: EventEmitter<CalendarUserEvent<D>> = new EventEmitter();

  /** 发出选定的月份。只是月份发现变化 */
  @Output() readonly monthSelected: EventEmitter<D> = new EventEmitter();

  /** 在激活任何日期时发出 */
  @Output() readonly activeDateChange: EventEmitter<D> = new EventEmitter<D>();

  @ViewChild(SimCalendarBodyComponent) _calendarBody: SimCalendarBodyComponent<D>;

  /** 月份的数据 */
  _months: CalendarCell[][] = [];

  /** 今天是今年当前的月份。 如果今天在不同的年份，则为Null。 */
  _todayMonth: number | null;

  /** 选定的日期是在今年的哪个月。如果选择的日期在不同的年份，则为Null。 */
  _selectedMonth: number | null;

  private _activeDate: D;
  private _minDate: D | null;
  private _maxDate: D | null;
  private _selected: DateRange<D> | D | null;

  constructor(private _changeDetectorRef: ChangeDetectorRef, @Optional() public _dateAdapter: SimDateAdapter<D>) {
    if (!this._dateAdapter && isDevMode) {
      throw createMissingDateImplError('SimDateAdapter');
    }
    this._activeDate = this._dateAdapter.today();
  }

  ngAfterContentInit() {
    this._init();
  }

  _focusActiveCell() {
    this._calendarBody._focusActiveCell();
  }

  /** 选择新的月份时处理 */
  _monthSelected(event: CalendarUserEvent<number>) {
    const month = event.value;
    const normalizedDate = this._dateAdapter.createDate(this._dateAdapter.getYear(this.activeDate), month, 1);

    this.monthSelected.emit(normalizedDate);

    if (this.picker === 'month') {
      this.userSelection.emit({
        ...event,
        ...{
          value: this._dateAdapter.createDate(
            this._dateAdapter.getYear(this.activeDate),
            month,
            this._dateAdapter.getDate(this.activeDate),
            this._dateAdapter.getHours(this.activeDate),
            this._dateAdapter.getMinutes(this.activeDate),
            this._dateAdapter.getSeconds(this.activeDate)
          )
        }
      });
      return;
    }

    const daysInMonth = this._dateAdapter.getNumDaysInMonth(normalizedDate);

    this.selectedChange.emit(
      this._dateAdapter.createDate(
        this._dateAdapter.getYear(this.activeDate),
        month,
        Math.min(this._dateAdapter.getDate(this.activeDate), daysInMonth)
      )
    );
  }

  /** 当日历处于多年视图中时，处理日历主体上的按键事件 */
  _handleCalendarBodyKeydown(event: KeyboardEvent) {}

  _init() {
    this._setSelectedMonth(this.selected);
    this._todayMonth = this._getMonthInCurrentYear(this._dateAdapter.today());

    const monthNames = this._dateAdapter.getMonthNames('short');

    this._months = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [9, 10, 11]
    ].map(row => row.map(month => this._createCellForMonth(month, monthNames[month])));
    this._changeDetectorRef.markForCheck();
  }

  private _createCellForMonth(month: number, monthName: string) {
    const date = this._dateAdapter.createDate(this._dateAdapter.getYear(this.activeDate), month, 1);
    const cellClasses = this.dateClass ? this.dateClass(date, 'year') : undefined;
    return new CalendarCell(month, monthName, monthName, this._shouldEnableMonth(month), cellClasses);
  }

  /** 月份的过滤器 */
  private _shouldEnableMonth(month: number) {
    const activeYear = this._dateAdapter.getYear(this.activeDate);

    if (
      month === undefined ||
      month === null ||
      this._isYearAndMonthAfterMaxDate(activeYear, month) ||
      this._isYearAndMonthBeforeMinDate(activeYear, month)
    ) {
      return false;
    }

    if (!this.dateFilter) {
      return true;
    }

    const firstOfMonth = this._dateAdapter.createDate(activeYear, month, 1);

    // 如果月中的任何日期是启用的，请将该月视为启用的
    for (let date = firstOfMonth; this._dateAdapter.getMonth(date) === month; date = this._dateAdapter.addDays(date, 1)) {
      if (this.dateFilter(date)) {
        return true;
      }
    }

    return false;
  }

  /**
   * 测试组合月份/年是否在此之后。maxDate，只考虑它的月份和年份
   */
  private _isYearAndMonthAfterMaxDate(year: number, month: number) {
    if (this.maxDate) {
      const maxYear = this._dateAdapter.getYear(this.maxDate);
      const maxMonth = this._dateAdapter.getMonth(this.maxDate);

      return year > maxYear || (year === maxYear && month > maxMonth);
    }

    return false;
  }

  /**
   * 测试组合月份/年是否在此之前。只考虑月份和年份
   */
  private _isYearAndMonthBeforeMinDate(year: number, month: number) {
    if (this.minDate) {
      const minYear = this._dateAdapter.getYear(this.minDate);
      const minMonth = this._dateAdapter.getMonth(this.minDate);

      return year < minYear || (year === minYear && month < minMonth);
    }

    return false;
  }

  private _getMonthInCurrentYear(date: D | null) {
    return date && this._dateAdapter.getYear(date) === this._dateAdapter.getYear(this.activeDate) ? this._dateAdapter.getMonth(date) : null;
  }

  /** 根据模型值设置当前选择的月份 */
  private _setSelectedMonth(value: DateRange<D> | D | null) {
    if (value instanceof DateRange) {
      this._selectedMonth = this._getMonthInCurrentYear(value.start) || this._getMonthInCurrentYear(value.end);
    } else {
      this._selectedMonth = this._getMonthInCurrentYear(value);
    }
  }
}
